import React, { useMemo } from 'react';
import TableCommonHook from '@op/components/TableCommonHook';
import { DBO_V } from '@op/utils/format';
import { LanguageType } from '@op/types/common';
import { TableColumn } from 'tea-component';
import useIntls from '@opm/hooks/useIntls';
import VideoInfoCell from '@opm/components/VideoInfoCell';
import MetricProgress from '@opm/components/MetricProgress';
import { DBOM_CONTENT } from '@opm/utils';
import {
  DOUYIN_SORT_OPTIONS,
  DOUYIN_TABLE_METRICS,
} from '../constants';
import './index.scss';
import BubbleCommon from '@op/components/BubbleCommon';

interface IDouyinVideoTableProps {
  sortBy: any;
  setSortBy: (value: any) => void;
  language: LanguageType;
  tableRecords?: any[];
  loading?: boolean;
  total?: number;
  pageInfo: any;
  changePageInfo: (value: any) => void;
  /** 各指标全局最大值（接口 data.max），用作 MetricProgress 分母 */
  max?: Record<string, number>;
}

const FIRST_COL_WIDTH = 490;
const METRIC_COL_WIDTH = 140;
const EMPTY_CELL = '-';

export default function DouyinVideoTable({
  sortBy,
  setSortBy,
  language,
  tableRecords = [],
  loading,
  total,
  pageInfo,
  changePageInfo,
  max = {},
}: IDouyinVideoTableProps) {
  const { intl, isEn } = useIntls();

  const columns: TableColumn<any>[] = useMemo(() => {
    const videoColumn: TableColumn<any> = {
      key: 'publish_time',
      header: intl({ id: 'Video (Sort by Date)' }),
      width: FIRST_COL_WIDTH,
      render: (data: any) => (
        <VideoInfoCell
          data={data}
          fieldMapping={{
            cover: 'video_cover',
            title: (d: any) => DBOM_CONTENT.videoTitle(language, d),
            channelName: () => 'douyin',
            authorName: 'author_name',
            publishTime: 'publish_time',
            videoUrl: 'video_url',
          }}
          desItems={[
            {
              type: 'text',
              field: (d: any) => DBOM_CONTENT.categoryGlobal(isEn, d),
              className: 'category',
            },
            { type: 'time', field: 'publish_time' },
          ]}
        />
      ),
    };

    const metricColumns: TableColumn<any>[] = DOUYIN_TABLE_METRICS.map(
      metric => ({
        key: metric.key,
        header: () => {
          if (metric.tipsMap) {
            return <BubbleCommon placement="top" tipsMap={metric.tipsMap}>{intl({ id: metric.label })}</BubbleCommon>;
          }
          return intl({ id: metric.label });
        },
        align: 'right',
        width: METRIC_COL_WIDTH,
        className: 'dbom-douyin-video-metric-cell',
        render: (cvm: any) => {
          const value = cvm?.[metric.key];
          const isEmpty =
            value === null || value === undefined || value === -1;
          return (
            <div>
              {isEmpty ? EMPTY_CELL : DBO_V.reviews(value)}
              <MetricProgress
                biggest={max[metric.key] || 0}
                count={isEmpty ? 0 : value}
              />
            </div>
          );
        },
      }),
    );

    return [videoColumn, ...metricColumns];
  }, [intl, language, max, isEn]);

  return (
    <TableCommonHook
      sortInfo={[sortBy, setSortBy]}
      records={tableRecords}
      total={total}
      loading={loading}
      pageInfo={pageInfo}
      setPageInfo={changePageInfo}
      sortColumns={DOUYIN_SORT_OPTIONS.map(option => option.value)}
      recordKey={(item: any) =>
        item?.video_id ||
        `${item?.video_title || ''}_${item?.publish_time || ''}_${item?.author_name || ''}`
      }
      columns={columns}
      minWidth="auto"
      tableSticky
      pageSizeOptions={[10, 20, 30, 40, 50, 100, 200]}
      className="dbom-douyin-video-table"
      verticalTop={false}
    />
  );
}
